import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { machinesService } from '../services/api';
import { formatDateForAPIFrom, formatDateForAPITo } from '../utils/dateUtils';

// Import MeasurementChart from ChargerDashboard
// Chart component for displaying measurement data
const MeasurementChart: React.FC<{
  title: string;
  data: (number | null)[];
  timestamps: Date[];
  unit: string;
  color: string;
  dateRange: { start: Date; end: Date };
  loading?: boolean;
  error?: string;
  onZoom?: (start: Date, end: Date) => void;
}> = ({ title, data, timestamps, unit, color, dateRange, loading, error, onZoom }) => {
  const theme = useTheme();
  
  // Tooltip state
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    value: number | null;
    timestamp: Date | null;
  }>({
    visible: false,
    x: 0,
    y: 0,
    value: null,
    timestamp: null,
  });

  // Vertical line state for mouse tracking
  const [verticalLine, setVerticalLine] = useState<{
    visible: boolean;
    x: number;
  }>({
    visible: false,
    x: 0,
  });

  // Selection state for zoom
  const [selection, setSelection] = useState<{
    isSelecting: boolean;
    startX: number | null;
    endX: number | null;
  }>({
    isSelecting: false,
    startX: null,
    endX: null,
  });
  
  // Helper function to get time range for calculations
  const getTimeRange = () => {
    const isSameDay = dateRange.start.toDateString() === dateRange.end.toDateString();
    let startMs: number, endMs: number, spanMs: number;
    
    if (isSameDay) {
      startMs = dateRange.start.getTime();
      endMs = dateRange.end.getTime();
      spanMs = Math.max(1, endMs - startMs);
    } else {
      startMs = dateRange.start.getTime();
      const endDate = new Date(dateRange.end);
      endDate.setDate(endDate.getDate() + 1);
      endDate.setHours(0, 0, 0, 0);
      endMs = endDate.getTime();
      spanMs = Math.max(1, endMs - startMs);
    }
    
    return { startMs, endMs, spanMs };
  };
  
  // Helper function to convert x position (percentage) to timestamp
  const xPercentToTimestamp = (xPercent: number): Date => {
    const { startMs, spanMs } = getTimeRange();
    const adjustedX = Math.max(0, Math.min(100, xPercent));
    const timestampMs = startMs + (adjustedX / 100) * spanMs;
    return new Date(timestampMs);
  };

  // Helper function to format timestamp for tooltip
  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleString('no-NO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };
  
  // Helper function to format value for tooltip
  const formatValue = (value: number, unit: string) => {
    if (unit === '%') {
      return `${value.toFixed(1)}%`;
    } else if (unit === 'kW' || unit === 'V' || unit === 'A' || unit === 'kWh') {
      return `${value.toFixed(2)} ${unit}`;
    } else if (unit === '°C') {
      return `${value.toFixed(1)}°C`;
    }
    return `${value.toFixed(2)} ${unit}`;
  };

  // Chart padding constants (needed for handleMouseMove)
  const chartPaddingLeftPct = 0;
  const chartPaddingRightPct = 0;
  const chartInnerWidthPct = 100 - chartPaddingLeftPct - chartPaddingRightPct;

  // Mouse down handler to start selection
  const handleMouseDown = (event: React.MouseEvent) => {
    // Only start selection on left mouse button
    if (event.button !== 0) return;
    
    // Prevent text selection during drag
    event.preventDefault();
    
    const chartContainer = event.currentTarget.closest('[data-chart-container]') as HTMLElement;
    if (chartContainer) {
      const rect = chartContainer.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const xPercent = (x / rect.width) * 100;
      
      setSelection({
        isSelecting: true,
        startX: xPercent,
        endX: xPercent,
      });
      
      // Hide tooltip and vertical line when selecting
      setVerticalLine(prev => ({ ...prev, visible: false }));
      setTooltip(prev => ({ ...prev, visible: false }));
    }
  };

  // Mouse move handler for vertical line and selection
  const handleMouseMove = (event: React.MouseEvent) => {
    const chartContainer = event.currentTarget.closest('[data-chart-container]') as HTMLElement;
    if (!chartContainer) return;
    
    const rect = chartContainer.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const xPercent = (x / rect.width) * 100;
    
    // If selecting, update selection end
    if (selection.isSelecting && selection.startX !== null) {
      setSelection(prev => ({
        ...prev,
        endX: xPercent,
      }));
      return;
    }
    
    // Otherwise, show tooltip (existing behavior)
    // Check if start and end dates are the same day
    const isSameDay = dateRange.start.toDateString() === dateRange.end.toDateString();
      
      let startMs: number, endMs: number, spanMs: number;
      
      if (isSameDay) {
        startMs = dateRange.start.getTime();
        endMs = dateRange.end.getTime();
        spanMs = Math.max(1, endMs - startMs);
      } else {
        startMs = dateRange.start.getTime();
        const endDate = new Date(dateRange.end);
        endDate.setDate(endDate.getDate() + 1);
        endDate.setHours(0, 0, 0, 0);
        endMs = endDate.getTime();
        spanMs = Math.max(1, endMs - startMs);
      }
      
      // Find the closest data point within a reasonable threshold (2% of chart width)
      const threshold = 2;
      let closestDataPoint = null;
      let closestDistance = Infinity;
      
      for (let i = 0; i < data.length; i++) {
        const t = timestamps[i]?.getTime();
        if (!t || t < startMs || t > endMs) continue;
        
        const dataPointX = chartPaddingLeftPct + (((t - startMs) / spanMs) * chartInnerWidthPct);
        const distance = Math.abs(xPercent - dataPointX);
        
        if (distance < threshold && distance < closestDistance) {
          closestDistance = distance;
          closestDataPoint = dataPointX;
        }
      }
      
      if (closestDataPoint !== null) {
        // Find the data point index that corresponds to this x position
        let dataPointIndex = -1;
        for (let i = 0; i < data.length; i++) {
          const t = timestamps[i]?.getTime();
          if (!t || t < startMs || t > endMs) continue;
          
          const dataPointX = chartPaddingLeftPct + (((t - startMs) / spanMs) * chartInnerWidthPct);
          if (Math.abs(closestDataPoint - dataPointX) < 0.1) {
            dataPointIndex = i;
            break;
          }
        }
        
        if (dataPointIndex >= 0) {
          const value = data[dataPointIndex];
          const timestamp = timestamps[dataPointIndex];
          
          setVerticalLine({
            visible: true,
            x: closestDataPoint,
          });
          
          setTooltip({
            visible: true,
            x: event.clientX - rect.left - 120,
            y: event.clientY - rect.top,
            value,
            timestamp,
          });
        }
      } else {
        setVerticalLine(prev => ({ ...prev, visible: false }));
        setTooltip(prev => ({ ...prev, visible: false }));
      }
  };

  // Mouse up handler to complete selection and zoom
  const handleMouseUp = (event: React.MouseEvent) => {
    if (!selection.isSelecting || selection.startX === null || selection.endX === null) {
      return;
    }
    
    const startX = Math.min(selection.startX, selection.endX);
    const endX = Math.max(selection.startX, selection.endX);
    
    // Only zoom if selection is meaningful (at least 1% of chart width)
    if (endX - startX > 1 && onZoom) {
      const startTimestamp = xPercentToTimestamp(startX);
      const endTimestamp = xPercentToTimestamp(endX);
      
      // Check if selection spans multiple days
      const startDate = new Date(startTimestamp);
      const endDate = new Date(endTimestamp);
      const isSameDay = startDate.toDateString() === endDate.toDateString();
      
      let newStart: Date, newEnd: Date;
      
      if (isSameDay) {
        // For same day, use the actual selected times
        newStart = new Date(startTimestamp);
        newEnd = new Date(endTimestamp);
      } else {
        // For multiple days, set start to beginning of start day and end to end of end day
        newStart = new Date(startTimestamp);
        newStart.setHours(0, 0, 0, 0);
        
        newEnd = new Date(endTimestamp);
        newEnd.setHours(23, 59, 59, 999);
      }
      
      onZoom(newStart, newEnd);
    }
    
    // Reset selection
    setSelection({
      isSelecting: false,
      startX: null,
      endX: null,
    });
  };

  const handleChartMouseLeave = () => {
    // If selecting, don't reset selection on mouse leave (allow dragging outside)
    if (!selection.isSelecting) {
      setVerticalLine(prev => ({ ...prev, visible: false }));
      setTooltip(prev => ({ ...prev, visible: false }));
    }
  };

  // Global mouse up handler to handle mouse up outside chart
  useEffect(() => {
    const handleGlobalMouseUp = (event: MouseEvent) => {
      if (selection.isSelecting) {
        // Reset selection if mouse up occurs outside
        setSelection({
          isSelecting: false,
          startX: null,
          endX: null,
        });
      }
    };

    if (selection.isSelecting) {
      window.addEventListener('mouseup', handleGlobalMouseUp);
      return () => {
        window.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [selection.isSelecting]);
  
  // Calculate dynamic min/max values based on actual data (ignore nulls)
  const definedData = data.filter((v): v is number => v !== null && !Number.isNaN(v));
  const dataMin = definedData.length ? Math.min(...definedData) : 0;
  const dataMax = definedData.length ? Math.max(...definedData) : 0;
  
  // Special handling for percentage data (0-100%)
  let minValue: number, maxValue: number, range: number;
  
  if (title.includes('SoC') || unit === '%') {
    minValue = 0;
    maxValue = 100;
    range = maxValue - minValue;
  } else {
    // For all other data types, apply rules similar to ChargerDashboard
    const zeroInRange = dataMin <= 0 && dataMax >= 0;
    
    let YMIN: number, YMAX: number;
    
    if (zeroInRange) {
      if (dataMin === 0) {
        const padding = Math.max(dataMax * 0.1, 1);
        const upperBound = dataMax + padding;
        const roughStep = upperBound / 4;
        let step = Math.ceil(roughStep);
        step = Math.ceil(step / 5) * 5;
        step = Math.max(step, 5);
        YMIN = 0;
        YMAX = 4 * step;
      } else {
        const absMax = Math.max(Math.abs(dataMin), Math.abs(dataMax));
        const padding = Math.max(absMax * 0.1, 1);
        const neededRange = Math.max(
          Math.abs(dataMin) + padding,
          Math.abs(dataMax) + padding
        );
        let step = Math.ceil(neededRange / 2);
        step = Math.ceil(step / 5) * 5;
        step = Math.max(step, 5);
        YMIN = -2 * step;
        YMAX = 2 * step;
      }
    } else {
      const dataRange = dataMax - dataMin;
      const padding = Math.max(dataRange * 0.1, Math.abs(dataMax) * 0.05, 1);
      const totalRange = dataRange + padding * 2;
      const roughStep = totalRange / 4;
      let step = Math.ceil(roughStep);
      step = Math.ceil(step / 5) * 5;
      step = Math.max(step, 5);
      let requiredRange = step * 4;
      const lowerBound = dataMin - padding;
      const upperBound = dataMax + padding;
      const center = (lowerBound + upperBound) / 2;
      const targetYMIN = center - requiredRange / 2;
      const targetYMINRounded = Math.floor(targetYMIN);
      const remainder = targetYMINRounded % 10;
      if (remainder <= 5) {
        YMIN = targetYMINRounded - remainder;
      } else {
        YMIN = targetYMINRounded - (remainder - 5);
      }
      YMAX = YMIN + requiredRange;
      if (YMAX < upperBound) {
        const minRequiredStep = Math.ceil((upperBound - YMIN) / 4 / 5) * 5;
        step = Math.max(minRequiredStep, step);
        requiredRange = step * 4;
        YMAX = YMIN + requiredRange;
      }
      if (YMIN > lowerBound) {
        const targetYMINNew = lowerBound;
        const targetYMINRoundedNew = Math.floor(targetYMINNew);
        const remainderNew = targetYMINRoundedNew % 10;
        let newYMIN: number;
        if (remainderNew <= 5) {
          newYMIN = targetYMINRoundedNew - remainderNew;
        } else {
          newYMIN = targetYMINRoundedNew - (remainderNew - 5);
        }
        const minStepForRange = Math.ceil((upperBound - newYMIN) / 4 / 5) * 5;
        step = Math.max(minStepForRange, step);
        YMIN = newYMIN;
        YMAX = YMIN + (step * 4);
      }
    }
    
    minValue = YMIN;
    maxValue = YMAX;
    range = maxValue - minValue;
  }
  
  // Generate Y-axis tick values dynamically
  const generateYAxisTicks = () => {
    if (title.includes('SoC') || unit === '%') {
      return [100, 75, 50, 25, 0];
    }
    const step = (maxValue - minValue) / 4;
    const ticks = [];
    for (let i = 4; i >= 0; i--) {
      ticks.push(Math.round(minValue + step * i));
    }
    return ticks;
  };
  
  const yAxisTicks = generateYAxisTicks();
  const chartPaddingBottomPct = 8;
  const chartPaddingTopPct = 5;
  const chartInnerHeightPct = 100 - chartPaddingBottomPct - chartPaddingTopPct;
  const yLabelRailWidthPx = 32;
  const yLabelGutterPx = 0;

  if (loading) {
    return (
      <Card sx={{ height: '100%', boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)' }}>
        <CardContent sx={{ p: '5px 16px 16px 16px' }}>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', fontSize: '14px', fontFamily: 'Montserrat', fontWeight: 400, lineHeight: '1.43', mb: 1 }}>
            {title}
          </Typography>
          <Box sx={{ height: 197, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={40} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)' }}>
        <CardContent sx={{ p: '5px 16px 16px 16px' }}>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', fontSize: '14px', fontFamily: 'Montserrat', fontWeight: 400, lineHeight: '1.43', mb: 1 }}>
            {title}
          </Typography>
          <Box sx={{ height: 197, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#f44336', fontFamily: 'Montserrat', mb: 1 }}>
              {error}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)', fontFamily: 'Montserrat', display: 'block', mb: 2 }}>
              Check the browser console for more details.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.location.reload()}
              sx={{ fontSize: '12px' }}
            >
              Retry
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0 || definedData.length === 0) {
    return (
      <Card sx={{ height: '100%', boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)' }}>
        <CardContent sx={{ p: '5px 16px 16px 16px' }}>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', fontSize: '14px', fontFamily: 'Montserrat', fontWeight: 400, lineHeight: '1.43', mb: 1 }}>
            {title}
          </Typography>
          <Box sx={{ height: 197, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', fontFamily: 'Montserrat', mb: 1 }}>
              No data available for the selected date range.
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)', fontFamily: 'Montserrat', display: 'block' }}>
              Try selecting a different date range.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)' }}>
      <CardContent sx={{ p: '5px 16px 16px 16px' }}>
        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', fontSize: '14px', fontFamily: 'Montserrat', fontWeight: 400, lineHeight: '1.43', mb: 1 }}>
          {title}
        </Typography>
        <Box sx={{ height: 197, position: 'relative' }}>
          <Box sx={{ height: 174, p: 2, position: 'relative' }}>
            <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${yLabelRailWidthPx}px`, pointerEvents: 'none' }}>
              {yAxisTicks.map((value, index) => {
                const relativeIndex = (index / (yAxisTicks.length - 1));
                const center = 0.5;
                const labelCompression = 0.8;
                const compressedRelative = center + (relativeIndex - center) * labelCompression;
                const y = chartPaddingTopPct + (compressedRelative * chartInnerHeightPct);
                const isEmphasis = index === 0 || index === yAxisTicks.length - 1 || value === 0;
                return (
                  <Typography
                    key={`ylabel-html-${index}`}
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      top: `${y}%`,
                      transform: 'translateY(-50%)',
                      right: '0px',
                      fontSize: '12px',
                      fontFamily: 'Montserrat',
                      color: isEmphasis ? 'rgba(0,0,0,0.87)' : 'rgba(0,0,0,0.6)',
                      lineHeight: '16px',
                      textAlign: 'right',
                    }}
                  >
                    {String(value)}
                  </Typography>
                );
              })}
            </Box>
            
            <Box 
              data-chart-container 
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleChartMouseLeave}
              sx={{ 
                ml: 3, 
                height: '100%', 
                position: 'relative', 
                pl: `${yLabelRailWidthPx + yLabelGutterPx}px`,
                cursor: selection.isSelecting ? 'col-resize' : 'default',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            >
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
                <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ vectorEffect: 'non-scaling-stroke' }}>
                  {yAxisTicks.map((_, index) => {
                    const y = chartPaddingTopPct + (index / (yAxisTicks.length - 1)) * chartInnerHeightPct;
                    return (
                      <line
                        key={index}
                        x1={0}
                        y1={y}
                        x2={100}
                        y2={y}
                        stroke="rgba(0,0,0,0.1)"
                        strokeWidth="1"
                        style={{ vectorEffect: 'non-scaling-stroke'}}
                      />
                    );
                  })}

                  <line
                    x1={0}
                    y1={chartPaddingTopPct}
                    x2={0}
                    y2={chartPaddingTopPct + chartInnerHeightPct}
                    stroke="rgba(0,0,0,0.3)"
                    strokeWidth="1"
                    style={{ vectorEffect: 'non-scaling-stroke' }}
                  />

                  {(() => {
                    const timeDiff = dateRange.end.getTime() - dateRange.start.getTime();
                    const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
                    const isSameDay = dateRange.start.toDateString() === dateRange.end.toDateString();
                    let actualStartMs: number, actualEndMs: number, actualSpanMs: number;
                    
                    if (isSameDay) {
                      actualStartMs = dateRange.start.getTime();
                      actualEndMs = dateRange.end.getTime();
                      actualSpanMs = Math.max(1, actualEndMs - actualStartMs);
                    } else {
                      actualStartMs = dateRange.start.getTime();
                      const endDate = new Date(dateRange.end);
                      endDate.setDate(endDate.getDate() + 1);
                      endDate.setHours(0, 0, 0, 0);
                      actualEndMs = endDate.getTime();
                      actualSpanMs = actualEndMs - actualStartMs;
                    }
                    
                    const ticks = [];
                    
                    if (isSameDay) {
                      const startHour = dateRange.start.getHours();
                      const endHour = dateRange.end.getHours();
                      const totalHours = endHour - startHour + 1;
                      let tickIntervalHours: number;
                      if (totalHours <= 2) {
                        tickIntervalHours = 1;
                      } else if (totalHours <= 6) {
                        tickIntervalHours = 1;
                      } else if (totalHours <= 12) {
                        tickIntervalHours = 2;
                      } else {
                        tickIntervalHours = 3;
                      }
                      
                      let currentHour = startHour;
                      let tickIndex = 0;
                      
                      while (currentHour <= endHour) {
                        const currentTime = new Date(dateRange.start);
                        currentTime.setHours(currentHour, 0, 0, 0);
                        const tickTime = currentTime.getTime();
                        const x = chartPaddingLeftPct + (((tickTime - actualStartMs) / actualSpanMs) * chartInnerWidthPct);
                        
                        ticks.push(
                          <line
                            key={`x-tick-${tickIndex}`}
                            x1={x}
                            y1={chartPaddingTopPct + chartInnerHeightPct - 2}
                            x2={x}
                            y2={chartPaddingTopPct + chartInnerHeightPct + 2}
                            stroke="rgba(0,0,0,0.6)"
                            strokeWidth="2"
                            style={{ vectorEffect: 'non-scaling-stroke' }}
                          />
                        );
                        
                        currentHour += tickIntervalHours;
                        tickIndex++;
                      }
                      
                      const endTime = new Date(dateRange.end);
                      endTime.setDate(endTime.getDate() + 1);
                      endTime.setHours(0, 0, 0, 0);
                      const endTimeMs = endTime.getTime();
                      const endX = chartPaddingLeftPct + (((endTimeMs - actualStartMs) / actualSpanMs) * chartInnerWidthPct);
                      
                      ticks.push(
                        <line
                          key={`x-tick-end`}
                          x1={endX}
                          y1={chartPaddingTopPct + chartInnerHeightPct - 2}
                          x2={endX}
                          y2={chartPaddingTopPct + chartInnerHeightPct + 2}
                          stroke="rgba(0,0,0,0.6)"
                          strokeWidth="2"
                          style={{ vectorEffect: 'non-scaling-stroke' }}
                        />
                      );
                    } else {
                      let tickIntervalDays: number;
                      if (daysDiff <= 1) {
                        tickIntervalDays = 1;
                      } else if (daysDiff <= 7) {
                        tickIntervalDays = 1;
                      } else if (daysDiff <= 14) {
                        tickIntervalDays = 2;
                      } else if (daysDiff <= 30) {
                        tickIntervalDays = 3;
                      } else if (daysDiff <= 60) {
                        tickIntervalDays = 5;
                      } else if (daysDiff <= 90) {
                        tickIntervalDays = 7;
                      } else {
                        tickIntervalDays = 14;
                      }
                      
                      const startDate = new Date(dateRange.start);
                      let currentDate = new Date(startDate);
                      let tickIndex = 0;
                      
                      while (currentDate.getTime() <= actualEndMs) {
                        const tickTime = currentDate.getTime();
                        const x = chartPaddingLeftPct + (((tickTime - actualStartMs) / actualSpanMs) * chartInnerWidthPct);
                        
                        ticks.push(
                          <line
                            key={`x-tick-${tickIndex}`}
                            x1={x}
                            y1={chartPaddingTopPct + chartInnerHeightPct - 2}
                            x2={x}
                            y2={chartPaddingTopPct + chartInnerHeightPct + 2}
                            stroke="rgba(0,0,0,0.6)"
                            strokeWidth="2"
                            style={{ vectorEffect: 'non-scaling-stroke' }}
                          />
                        );
                        
                        currentDate.setDate(currentDate.getDate() + tickIntervalDays);
                        tickIndex++;
                      }
                    }
                    
                    return ticks;
                  })()}
                  
                  {/* Selection rectangle for zoom */}
                  {selection.isSelecting && selection.startX !== null && selection.endX !== null && (
                    <rect
                      x={Math.min(selection.startX, selection.endX)}
                      y={chartPaddingTopPct}
                      width={Math.abs(selection.endX - selection.startX)}
                      height={chartInnerHeightPct}
                      fill="rgba(25, 118, 210, 0.1)"
                      stroke="rgba(25, 118, 210, 0.5)"
                      strokeWidth="1"
                      style={{ pointerEvents: 'none' }}
                    />
                  )}

                  {/* Vertical line for mouse tracking */}
                  {verticalLine.visible && !selection.isSelecting && (
                    <line
                      x1={verticalLine.x}
                      y1={chartPaddingTopPct}
                      x2={verticalLine.x}
                      y2={chartPaddingTopPct + chartInnerHeightPct}
                      stroke="#B5AFA9"
                      strokeWidth="1"
                      style={{ vectorEffect: 'non-scaling-stroke' }}
                    />
                  )}
                  
                  {(() => {
                    const segments: string[] = [];
                    let current: string[] = [];
                    const isSameDay = dateRange.start.toDateString() === dateRange.end.toDateString();
                    let startMs: number, endMs: number, spanMs: number;
                    
                    if (isSameDay) {
                      startMs = dateRange.start.getTime();
                      endMs = dateRange.end.getTime();
                      spanMs = Math.max(1, endMs - startMs);
                    } else {
                      startMs = dateRange.start.getTime();
                      const endDate = new Date(dateRange.end);
                      endDate.setDate(endDate.getDate() + 1);
                      endDate.setHours(0, 0, 0, 0);
                      endMs = endDate.getTime();
                      spanMs = Math.max(1, endMs - startMs);
                    }
                    for (let i = 0; i < data.length; i++) {
                      const value = data[i];
                      const t = timestamps[i]?.getTime();
                      if (!t || t < startMs || t > endMs) {
                        if (current.length > 0) {
                          segments.push(current.join(' '));
                          current = [];
                        }
                        continue;
                      }
                      const x = chartPaddingLeftPct + (((t - startMs) / spanMs) * chartInnerWidthPct);
                      if (typeof value === 'number') {
                        const y = chartPaddingTopPct + (1 - ((value - minValue) / range)) * chartInnerHeightPct;
                        if (current.length === 0) {
                          current.push(`M ${x},${y}`);
                        } else {
                          current.push(`L ${x},${y}`);
                        }
                      } else if (current.length > 0) {
                        segments.push(current.join(' '));
                        current = [];
                      }
                    }
                    if (current.length > 0) segments.push(current.join(' '));
                    return segments.map((d, idx) => (
                      <path
                        key={`line-${idx}`}
                        d={d}
                        fill="none"
                        stroke={theme.palette.secondary.main}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ vectorEffect: 'non-scaling-stroke' }}
                      />
                    ));
                  })()}
                </svg>
              </Box>
              
              {tooltip.visible && tooltip.timestamp && !selection.isSelecting && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: tooltip.x + 10,
                    top: tooltip.y + 10,
                    backgroundColor: '#413C38',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: 'Montserrat',
                    zIndex: 1000,
                    pointerEvents: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    maxWidth: '240px',
                  }}
                >
                  <Box sx={{ fontWeight: 600, mb: 0.5, fontSize: '11px', opacity: 0.9 }}>
                    {formatTimestamp(tooltip.timestamp)}
                  </Box>
                  {tooltip.value !== null && (
                    <Box sx={{ mb: 0.5 }}>
                      {title}: {formatValue(tooltip.value, unit)}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          
          <Box sx={{ position: 'relative', ml: 5, pl: `${yLabelRailWidthPx + yLabelGutterPx}px`, mr: 2, pb: 1, height: '20px' }}>
            {(() => {
              const timeDiff = dateRange.end.getTime() - dateRange.start.getTime();
              const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
              const isSameDay = dateRange.start.toDateString() === dateRange.end.toDateString();
              let actualStartMs: number, actualEndMs: number, actualSpanMs: number;
              
              if (isSameDay) {
                actualStartMs = dateRange.start.getTime();
                actualEndMs = dateRange.end.getTime();
                actualSpanMs = Math.max(1, actualEndMs - actualStartMs);
              } else {
                actualStartMs = dateRange.start.getTime();
                const endDate = new Date(dateRange.end);
                endDate.setDate(endDate.getDate() + 1);
                endDate.setHours(0, 0, 0, 0);
                actualEndMs = endDate.getTime();
                actualSpanMs = actualEndMs - actualStartMs;
              }
              
              const labels = [];
              
              if (isSameDay) {
                const startHour = dateRange.start.getHours();
                const endHour = dateRange.end.getHours();
                const totalHours = endHour - startHour + 1;
                let tickIntervalHours: number;
                if (totalHours <= 2) {
                  tickIntervalHours = 1;
                } else if (totalHours <= 6) {
                  tickIntervalHours = 1;
                } else if (totalHours <= 12) {
                  tickIntervalHours = 2;
                } else {
                  tickIntervalHours = 3;
                }
                
                const formatTime = (date: Date) => {
                  const hours = date.getHours().toString().padStart(2, '0');
                  const minutes = date.getMinutes().toString().padStart(2, '0');
                  return `${hours}:${minutes}`;
                };
                
                let currentHour = startHour;
                let labelIndex = 0;
                
                while (currentHour <= endHour) {
                  const currentTime = new Date(dateRange.start);
                  currentTime.setHours(currentHour, 0, 0, 0);
                  const tickTime = currentTime.getTime();
                  const x = chartPaddingLeftPct + (((tickTime - actualStartMs) / actualSpanMs) * chartInnerWidthPct);
                  
                  labels.push(
                    <Typography
                      key={`x-label-${labelIndex}`}
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        left: `${x}%`,
                        transform: 'translateX(-50%)',
                        fontSize: '12px',
                        fontFamily: 'Montserrat',
                        color: 'rgba(0,0,0,0.6)',
                        lineHeight: '12px',
                        letterSpacing: '0.15px',
                      }}
                    >
                      {formatTime(currentTime)}
                    </Typography>
                  );
                  
                  currentHour += tickIntervalHours;
                  labelIndex++;
                }
                
                const endTime = new Date(dateRange.end);
                endTime.setDate(endTime.getDate() + 1);
                endTime.setHours(0, 0, 0, 0);
                const endTimeMs = endTime.getTime();
                const endX = chartPaddingLeftPct + (((endTimeMs - actualStartMs) / actualSpanMs) * chartInnerWidthPct);
                
                labels.push(
                  <Typography
                    key={`x-label-end`}
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      left: `${endX}%`,
                      transform: 'translateX(-50%)',
                      fontSize: '12px',
                      fontFamily: 'Montserrat',
                      color: 'rgba(0,0,0,0.6)',
                      lineHeight: '12px',
                      letterSpacing: '0.15px',
                    }}
                  >
                    {formatTime(endTime)}
                  </Typography>
                );
              } else {
                let tickIntervalDays: number;
                if (daysDiff <= 1) {
                  tickIntervalDays = 1;
                } else if (daysDiff <= 7) {
                  tickIntervalDays = 1;
                } else if (daysDiff <= 14) {
                  tickIntervalDays = 2;
                } else if (daysDiff <= 30) {
                  tickIntervalDays = 3;
                } else if (daysDiff <= 60) {
                  tickIntervalDays = 5;
                } else if (daysDiff <= 90) {
                  tickIntervalDays = 7;
                } else {
                  tickIntervalDays = 14;
                }
                
                const formatDate = (date: Date) => {
                  const day = date.getDate();
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  const month = monthNames[date.getMonth()];
                  return `${day} ${month}`;
                };
                
                const startDate = new Date(dateRange.start);
                let currentDate = new Date(startDate);
                let labelIndex = 0;
                
                while (currentDate.getTime() <= actualEndMs) {
                  const tickTime = currentDate.getTime();
                  const x = chartPaddingLeftPct + (((tickTime - actualStartMs) / actualSpanMs) * chartInnerWidthPct);
                  
                  labels.push(
                    <Typography
                      key={`x-label-${labelIndex}`}
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        left: `${x}%`,
                        transform: 'translateX(-50%)',
                        fontSize: '12px',
                        fontFamily: 'Montserrat',
                        color: 'rgba(0,0,0,0.6)',
                        lineHeight: '12px',
                        letterSpacing: '0.15px',
                      }}
                    >
                      {formatDate(currentDate)}
                    </Typography>
                  );
                  
                  currentDate.setDate(currentDate.getDate() + tickIntervalDays);
                  labelIndex++;
                }
              }
              
              return labels;
            })()}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const MachineDashboard: React.FC = () => {
  const theme = useTheme();
  const { unitId, orgId } = useParams<{ unitId: string; orgId: string }>();
  const [showMoreCharts, setShowMoreCharts] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_machineName, setMachineName] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_machineModel, setMachineModel] = useState<string | null>(null);
  const [machineNameLoading, setMachineNameLoading] = useState(true);
  
  // Date range state - default to today 00:00 to today 23:59:59
  const [dateRange, setDateRange] = useState(() => {
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    
    return { start: startDate, end: endDate };
  });

  // Store the original date range (from date pickers, not from zoom)
  const [originalDateRange, setOriginalDateRange] = useState(() => {
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    
    return { start: startDate, end: endDate };
  });

  // Check if currently zoomed (date range differs from original)
  const isZoomed = useMemo(() => {
    return (
      dateRange.start.getTime() !== originalDateRange.start.getTime() ||
      dateRange.end.getTime() !== originalDateRange.end.getTime()
    );
  }, [dateRange, originalDateRange]);

  // Reset zoom function
  const handleResetZoom = useCallback(() => {
    setDateRange({
      start: new Date(originalDateRange.start),
      end: new Date(originalDateRange.end),
    });
  }, [originalDateRange]);

  // Handle zoom from chart selection (doesn't update original date range)
  const handleZoom = useCallback((start: Date, end: Date) => {
    setDateRange({ start, end });
    // Don't update originalDateRange - zoom should be resettable
  }, []);

  // Refs to prevent duplicate API calls
  const machineNameFetchedRef = useRef<string | null>(null);
  const mainChartsFetchedRef = useRef<string | null>(null);
  const additionalChartsFetchedRef = useRef<string | null>(null);

  // Main charts data states
  const [batterySOCData, setBatterySOCData] = useState<{ values: (number | null)[]; timestamps: Date[] }>({ values: [], timestamps: [] });
  const [batterySOCLoading, setBatterySOCLoading] = useState(false);
  const [batterySOCError, setBatterySOCError] = useState<string | null>(null);

  const [batteryCurrentData, setBatteryCurrentData] = useState<{ values: (number | null)[]; timestamps: Date[] }>({ values: [], timestamps: [] });
  const [batteryCurrentLoading, setBatteryCurrentLoading] = useState(false);
  const [batteryCurrentError, setBatteryCurrentError] = useState<string | null>(null);

  const [ccsPowerData, setCcsPowerData] = useState<{ values: (number | null)[]; timestamps: Date[] }>({ values: [], timestamps: [] });
  const [ccsPowerLoading, setCcsPowerLoading] = useState(false);
  const [ccsPowerError, setCcsPowerError] = useState<string | null>(null);

  const [acPowerData, setAcPowerData] = useState<{ values: (number | null)[]; timestamps: Date[] }>({ values: [], timestamps: [] });
  const [acPowerLoading, setAcPowerLoading] = useState(false);
  const [acPowerError, setAcPowerError] = useState<string | null>(null);

  // Additional charts data states (SHOW MORE)
  const [batteryTemperatureData, setBatteryTemperatureData] = useState<{ values: (number | null)[]; timestamps: Date[] }>({ values: [], timestamps: [] });
  const [batteryTemperatureLoading, setBatteryTemperatureLoading] = useState(false);
  const [batteryTemperatureError, setBatteryTemperatureError] = useState<string | null>(null);

  const [temperatureData, setTemperatureData] = useState<{ values: (number | null)[]; timestamps: Date[] }>({ values: [], timestamps: [] });
  const [temperatureLoading, setTemperatureLoading] = useState(false);
  const [temperatureError, setTemperatureError] = useState<string | null>(null);

  const [inverterPowerData, setInverterPowerData] = useState<{ values: (number | null)[]; timestamps: Date[] }>({ values: [], timestamps: [] });
  const [inverterPowerLoading, setInverterPowerLoading] = useState(false);
  const [inverterPowerError, setInverterPowerError] = useState<string | null>(null);

  const [ccsCurrentData, setCcsCurrentData] = useState<{ values: (number | null)[]; timestamps: Date[] }>({ values: [], timestamps: [] });
  const [ccsCurrentLoading, setCcsCurrentLoading] = useState(false);
  const [ccsCurrentError, setCcsCurrentError] = useState<string | null>(null);

  // Fetch machine details
  useEffect(() => {
    const dependencyKey = `${unitId}-${orgId}`;
    
    if (machineNameFetchedRef.current === dependencyKey) return;
    machineNameFetchedRef.current = dependencyKey;

    const fetchMachineName = async () => {
      if (!unitId || !orgId) {
        setMachineNameLoading(false);
        return;
      }

      try {
        setMachineNameLoading(true);
        const response = await machinesService.getMachines(orgId);
        if (response.success && response.data && response.data.machines) {
          const machine = response.data.machines.find(m => m.unit_id === unitId);
          if (machine) {
            setMachineName(machine.unit_name);
            setMachineModel(machine.model);
          } else {
            setMachineName(null);
            setMachineModel(null);
          }
        } else {
          setMachineName(null);
          setMachineModel(null);
        }
      } catch (error) {
        console.error('Error fetching machine name:', error);
        setMachineName(null);
        setMachineModel(null);
      } finally {
        setMachineNameLoading(false);
      }
    };

    fetchMachineName();
  }, [unitId, orgId]);

  // Fetch main chart data
  useEffect(() => {
    const dependencyKey = `${unitId}-${orgId}-${dateRange.start.getTime()}-${dateRange.end.getTime()}`;
    
    if (mainChartsFetchedRef.current === dependencyKey) return;
    mainChartsFetchedRef.current = dependencyKey;

    const fetchChartData = async () => {
      if (!unitId || !orgId) return;

      setBatterySOCLoading(true);
      setBatteryCurrentLoading(true);
      setCcsPowerLoading(true);
      setAcPowerLoading(true);
      setBatterySOCError(null);
      setBatteryCurrentError(null);
      setCcsPowerError(null);
      setAcPowerError(null);

      try {
        const fromDate = formatDateForAPIFrom(dateRange.start);
        const toDate = formatDateForAPITo(dateRange.end);

        // Fetch Battery SOC
        const batterySOCResponse = await machinesService.getMachineBatterySOC(orgId, unitId, fromDate, toDate);
        if (batterySOCResponse.success && batterySOCResponse.data) {
          const values = batterySOCResponse.data.data.map((point: any) => point.battery_soc ?? null);
          const timestamps = batterySOCResponse.data.data.map((point: any) => new Date(point.timestamp));
          setBatterySOCData({ values, timestamps });
        } else {
          setBatterySOCError(batterySOCResponse.message || 'Failed to fetch Battery SOC data');
        }

        // Fetch Battery Current
        const batteryCurrentResponse = await machinesService.getMachineBatteryCurrent(orgId, unitId, fromDate, toDate);
        if (batteryCurrentResponse.success && batteryCurrentResponse.data) {
          const values = batteryCurrentResponse.data.data.map((point: any) => point.battery_current ?? null);
          const timestamps = batteryCurrentResponse.data.data.map((point: any) => new Date(point.timestamp));
          setBatteryCurrentData({ values, timestamps });
        } else {
          setBatteryCurrentError(batteryCurrentResponse.message || 'Failed to fetch Battery Current data');
        }

        // Fetch CCS Power
        const ccsPowerResponse = await machinesService.getMachineCCSPower(orgId, unitId, fromDate, toDate);
        if (ccsPowerResponse.success && ccsPowerResponse.data) {
          const values = ccsPowerResponse.data.data.map((point: any) => point.ccs_power ?? null);
          const timestamps = ccsPowerResponse.data.data.map((point: any) => new Date(point.timestamp));
          setCcsPowerData({ values, timestamps });
        } else {
          setCcsPowerError(ccsPowerResponse.message || 'Failed to fetch CCS Power data');
        }

        // Fetch AC Power
        const acPowerResponse = await machinesService.getMachineACPower(orgId, unitId, fromDate, toDate);
        if (acPowerResponse.success && acPowerResponse.data) {
          const values = acPowerResponse.data.data.map((point: any) => point.ac_power ?? null);
          const timestamps = acPowerResponse.data.data.map((point: any) => new Date(point.timestamp));
          setAcPowerData({ values, timestamps });
        } else {
          setAcPowerError(acPowerResponse.message || 'Failed to fetch AC Power data');
        }
      } catch (error) {
        console.error('Error fetching chart data:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        setBatterySOCError(errorMessage);
        setBatteryCurrentError(errorMessage);
        setCcsPowerError(errorMessage);
        setAcPowerError(errorMessage);
      } finally {
        setBatterySOCLoading(false);
        setBatteryCurrentLoading(false);
        setCcsPowerLoading(false);
        setAcPowerLoading(false);
      }
    };

    fetchChartData();
  }, [dateRange, unitId, orgId]);

  // Fetch additional charts when "Show more" is clicked
  useEffect(() => {
    if (!showMoreCharts) {
      return;
    }

    const dependencyKey = `${unitId}-${orgId}-${dateRange.start.getTime()}-${dateRange.end.getTime()}-${showMoreCharts}`;
    
    if (additionalChartsFetchedRef.current === dependencyKey) return;
    additionalChartsFetchedRef.current = dependencyKey;

    const fetchAdditionalData = async () => {
      if (!unitId || !orgId) return;

      setBatteryTemperatureLoading(true);
      setTemperatureLoading(true);
      setInverterPowerLoading(true);
      setCcsCurrentLoading(true);
      setBatteryTemperatureError(null);
      setTemperatureError(null);
      setInverterPowerError(null);
      setCcsCurrentError(null);

      try {
        const fromDate = formatDateForAPIFrom(dateRange.start);
        const toDate = formatDateForAPITo(dateRange.end);

        // Fetch Battery Temperature
        const batteryTemperatureResponse = await machinesService.getMachineBatteryTemperature(orgId, unitId, fromDate, toDate);
        if (batteryTemperatureResponse.success && batteryTemperatureResponse.data) {
          const values = batteryTemperatureResponse.data.data.map((point: any) => point.battery_temperature ?? null);
          const timestamps = batteryTemperatureResponse.data.data.map((point: any) => new Date(point.timestamp));
          setBatteryTemperatureData({ values, timestamps });
        } else {
          setBatteryTemperatureError(batteryTemperatureResponse.message || 'Failed to fetch Battery Temperature data');
        }

        // Fetch Machine Temperature
        const temperatureResponse = await machinesService.getMachineTemperature(orgId, unitId, fromDate, toDate);
        if (temperatureResponse.success && temperatureResponse.data) {
          const values = temperatureResponse.data.data.map((point: any) => point.temperature ?? null);
          const timestamps = temperatureResponse.data.data.map((point: any) => new Date(point.timestamp));
          setTemperatureData({ values, timestamps });
        } else {
          setTemperatureError(temperatureResponse.message || 'Failed to fetch Machine Temperature data');
        }

        // Fetch Inverter Power
        const inverterPowerResponse = await machinesService.getMachineInverterPower(orgId, unitId, fromDate, toDate);
        if (inverterPowerResponse.success && inverterPowerResponse.data) {
          const values = inverterPowerResponse.data.data.map((point: any) => point.inverter_power ?? null);
          const timestamps = inverterPowerResponse.data.data.map((point: any) => new Date(point.timestamp));
          setInverterPowerData({ values, timestamps });
        } else {
          setInverterPowerError(inverterPowerResponse.message || 'Failed to fetch Inverter Power data');
        }

        // Fetch CCS Current
        const ccsCurrentResponse = await machinesService.getMachineCCSCurrent(orgId, unitId, fromDate, toDate);
        if (ccsCurrentResponse.success && ccsCurrentResponse.data) {
          const values = ccsCurrentResponse.data.data.map((point: any) => point.ccs_current ?? null);
          const timestamps = ccsCurrentResponse.data.data.map((point: any) => new Date(point.timestamp));
          setCcsCurrentData({ values, timestamps });
        } else {
          setCcsCurrentError(ccsCurrentResponse.message || 'Failed to fetch CCS Current data');
        }
      } catch (error) {
        console.error('Error fetching additional chart data:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        setBatteryTemperatureError(errorMessage);
        setTemperatureError(errorMessage);
        setInverterPowerError(errorMessage);
        setCcsCurrentError(errorMessage);
      } finally {
        setBatteryTemperatureLoading(false);
        setTemperatureLoading(false);
        setInverterPowerLoading(false);
        setCcsCurrentLoading(false);
      }
    };

    fetchAdditionalData();
  }, [showMoreCharts, dateRange, unitId, orgId]);

  // Format date for input value
  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Dashboard
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', mb: 3, gap: 2 }}>
        <Box sx={{ position: 'relative' }}>
          <input
            type="date"
            value={formatDateForInput(dateRange.start)}
            onChange={(e) => {
              if (e.target.value) {
                const startDate = new Date(e.target.value);
                startDate.setHours(0, 0, 0, 0);
                setDateRange(prev => ({ ...prev, start: startDate }));
                // Update original date range when user manually changes date picker
                setOriginalDateRange(prev => ({ ...prev, start: startDate }));
              }
            }}
            style={{
              width: '150px',
              height: '40px',
              padding: '8px 12px',
              fontSize: '16px',
              fontFamily: 'Montserrat',
              border: '1px solid rgba(0,0,0,0.23)',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              color: 'rgba(0,0,0,0.87)',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#1976d2';
              e.target.style.borderWidth = '2px';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(0,0,0,0.23)';
              e.target.style.borderWidth = '1px';
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: '-8px',
              left: '12px',
              backgroundColor: '#FAFAFA',
              padding: '0 4px',
              fontSize: '12px',
              color: 'rgba(0,0,0,0.6)',
              fontFamily: 'Montserrat',
            }}
          >
            Start Date
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', fontFamily: 'Montserrat' }}>
          –
        </Typography>
        <Box sx={{ position: 'relative' }}>
          <input
            type="date"
            value={formatDateForInput(dateRange.end)}
            onChange={(e) => {
              if (e.target.value) {
                const endDate = new Date(e.target.value);
                endDate.setHours(23, 59, 59, 999);
                setDateRange(prev => ({ ...prev, end: endDate }));
                // Update original date range when user manually changes date picker
                setOriginalDateRange(prev => ({ ...prev, end: endDate }));
              }
            }}
            style={{
              width: '150px',
              height: '40px',
              padding: '8px 12px',
              fontSize: '16px',
              fontFamily: 'Montserrat',
              border: '1px solid rgba(0,0,0,0.23)',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              color: 'rgba(0,0,0,0.87)',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#1976d2';
              e.target.style.borderWidth = '2px';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(0,0,0,0.23)';
              e.target.style.borderWidth = '1px';
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: '-8px',
              left: '12px',
              backgroundColor: '#FAFAFA',
              padding: '0 4px',
              fontSize: '12px',
              color: 'rgba(0,0,0,0.6)',
              fontFamily: 'Montserrat',
            }}
          >
            End Date
          </Typography>
        </Box>
        {isZoomed && (
          <Button
            variant="outlined"
            onClick={handleResetZoom}
            sx={{
              borderColor: 'rgba(51,122,92,0.5)',
              color: '#337a5c',
              fontSize: '14px',
              fontFamily: 'Montserrat',
              fontWeight: 500,
              letterSpacing: '0.46px',
              textTransform: 'none',
              px: 2,
              py: 1,
              height: '40px',
            }}
          >
            Reset zoom
          </Button>
        )}
      </Box>

      {/* Charts section */}
      {machineNameLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          {/* Charts grid */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <MeasurementChart
                title="Battery level [%]"
                data={batterySOCData.values}
                timestamps={batterySOCData.timestamps}
                unit="%"
                color="#2C3D5A"
                dateRange={dateRange}
                loading={batterySOCLoading}
                error={batterySOCError || undefined}
                onZoom={handleZoom}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MeasurementChart
                title="Battery current [A]"
                data={batteryCurrentData.values}
                timestamps={batteryCurrentData.timestamps}
                unit="A"
                color="#2C3D5A"
                dateRange={dateRange}
                loading={batteryCurrentLoading}
                error={batteryCurrentError || undefined}
                onZoom={handleZoom}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MeasurementChart
                title="CCS power [kW]"
                data={ccsPowerData.values}
                timestamps={ccsPowerData.timestamps}
                unit="kW"
                color="#2C3D5A"
                dateRange={dateRange}
                loading={ccsPowerLoading}
                error={ccsPowerError || undefined}
                onZoom={handleZoom}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MeasurementChart
                title="AC power [kW]"
                data={acPowerData.values}
                timestamps={acPowerData.timestamps}
                unit="kW"
                color="#2C3D5A"
                dateRange={dateRange}
                loading={acPowerLoading}
                error={acPowerError || undefined}
                onZoom={handleZoom}
              />
            </Grid>
          </Grid>

          {/* Additional charts - shown when "Show more" is clicked */}
          {showMoreCharts && (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <MeasurementChart
                    title="Battery temperature [°C]"
                    data={batteryTemperatureData.values}
                    timestamps={batteryTemperatureData.timestamps}
                    unit="°C"
                    color="#2C3D5A"
                    dateRange={dateRange}
                    loading={batteryTemperatureLoading}
                    error={batteryTemperatureError || undefined}
                    onZoom={handleZoom}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MeasurementChart
                    title="Machine temperature [°C]"
                    data={temperatureData.values}
                    timestamps={temperatureData.timestamps}
                    unit="°C"
                    color="#2C3D5A"
                    dateRange={dateRange}
                    loading={temperatureLoading}
                    error={temperatureError || undefined}
                    onZoom={handleZoom}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MeasurementChart
                    title="Inverter Power [kW]"
                    data={inverterPowerData.values}
                    timestamps={inverterPowerData.timestamps}
                    unit="kW"
                    color="#2C3D5A"
                    dateRange={dateRange}
                    loading={inverterPowerLoading}
                    error={inverterPowerError || undefined}
                    onZoom={handleZoom}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MeasurementChart
                    title="CCS current [A]"
                    data={ccsCurrentData.values}
                    timestamps={ccsCurrentData.timestamps}
                    unit="A"
                    color="#2C3D5A"
                    dateRange={dateRange}
                    loading={ccsCurrentLoading}
                    error={ccsCurrentError || undefined}
                    onZoom={handleZoom}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Show more button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setShowMoreCharts(!showMoreCharts)}
              sx={{
                borderColor: 'rgba(51,122,92,0.5)',
                color: '#337a5c',
                fontSize: '16px',
                fontFamily: 'Montserrat',
                fontWeight: 500,
                letterSpacing: '0.46px',
                textTransform: 'uppercase',
                px: 6,
                py: 1,
              }}
            >
              {showMoreCharts ? 'show less' : 'show more'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MachineDashboard;

